import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Paper,
    LinearProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Divider
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Assignment as FormsIcon,
    School as ApplicationIcon,
    People as UsersIcon,
    TrendingUp,
    Refresh,
    Visibility,
    CheckCircle,
    Schedule,
    Warning
} from '@mui/icons-material';
import { useAuth } from '../users/AuthContext';
import { APIApplicationFormList, APIAppUnderGraduateList } from '../utilities/APIs';

const DashboardOverview = () => {
    const { makeAuthenticatedRequest } = useAuth();
    const [stats, setStats] = useState({
        totalForms: 0,
        publishedForms: 0,
        totalApplications: 0,
        pendingApplications: 0
    });
    const [recentForms, setRecentForms] = useState([]);
    const [recentApplications, setRecentApplications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            // Fetch forms data
            const formsResponse = await makeAuthenticatedRequest(APIApplicationFormList);
            const forms = formsResponse.data || [];
            
            // Fetch applications data
            const applicationsResponse = await makeAuthenticatedRequest(APIAppUnderGraduateList);
            const applications = applicationsResponse.data || [];

            // Calculate statistics
            const publishedForms = forms.filter(form => form.isPublished === true).length;
            
            setStats({
                totalForms: forms.length,
                publishedForms: publishedForms,
                totalApplications: applications.length,
                pendingApplications: applications.length // Assuming all are pending for now
            });

            // Set recent data (last 5 items)
            setRecentForms(forms.slice(0, 5));
            setRecentApplications(applications.slice(0, 5));

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, subtitle, icon, color = '#667eea', trend }) => (
        <Card 
            sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', mr: 2 }}>
                        {icon}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            {loading ? '...' : value}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {title}
                        </Typography>
                    </Box>
                </Box>
                
                {subtitle && (
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {subtitle}
                    </Typography>
                )}

                {trend && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} />
                        <Typography variant="body2">
                            {trend}
                        </Typography>
                    </Box>
                )}

                {/* Decorative background element */}
                <Box 
                    sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        zIndex: 0
                    }}
                />
            </CardContent>
        </Card>
    );

    const getFormStatusChip = (isPublished) => (
        <Chip
            label={isPublished ? 'Published' : 'Draft'}
            color={isPublished ? 'success' : 'warning'}
            size="small"
            icon={isPublished ? <CheckCircle /> : <Schedule />}
        />
    );

    return (
        <Box sx={{ p: 3 }}>
            {/* Welcome Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
                    Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Welcome to the University Management System. Here's an overview of your system activity.
                </Typography>
            </Box>

            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Forms"
                        value={stats.totalForms}
                        subtitle="All forms in system"
                        icon={<FormsIcon />}
                        color="#667eea"
                        trend="+12% from last month"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Published Forms"
                        value={stats.publishedForms}
                        subtitle="Available for applications"
                        icon={<CheckCircle />}
                        color="#48bb78"
                        trend={`${stats.totalForms > 0 ? Math.round((stats.publishedForms / stats.totalForms) * 100) : 0}% published`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Applications"
                        value={stats.totalApplications}
                        subtitle="Total submissions"
                        icon={<ApplicationIcon />}
                        color="#ed8936"
                        trend="+5 this week"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Active Users"
                        value="24"
                        subtitle="Currently online"
                        icon={<UsersIcon />}
                        color="#9f7aea"
                        trend="Real-time"
                    />
                </Grid>
            </Grid>

            {/* Content Grid */}
            <Grid container spacing={3}>
                {/* Recent Forms */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Recent Forms
                                </Typography>
                                <IconButton size="small" onClick={fetchDashboardData}>
                                    <Refresh />
                                </IconButton>
                            </Box>
                            
                            {loading ? (
                                <LinearProgress />
                            ) : recentForms.length === 0 ? (
                                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                                    No forms available
                                </Typography>
                            ) : (
                                <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                                    {recentForms.map((form, index) => {
                                        const formData = typeof form.formConfigJson === 'string' 
                                            ? JSON.parse(form.formConfigJson) 
                                            : form.formConfigJson;
                                        return (
                                            <React.Fragment key={form.id}>
                                                <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                                                    <ListItemAvatar>
                                                        <Avatar sx={{ bgcolor: '#667eea' }}>
                                                            <FormsIcon />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                                    {formData?.name || 'Unnamed Form'}
                                                                </Typography>
                                                                {getFormStatusChip(form.isPublished)}
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formData?.type || 'Unknown Type'} • Created {new Date(form.createdAt).toLocaleDateString()}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                                {index < recentForms.length - 1 && <Divider variant="inset" component="li" />}
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Recent Applications */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Recent Applications
                                </Typography>
                                <IconButton size="small" onClick={fetchDashboardData}>
                                    <Refresh />
                                </IconButton>
                            </Box>
                            
                            {loading ? (
                                <LinearProgress />
                            ) : recentApplications.length === 0 ? (
                                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                                    No applications submitted yet
                                </Typography>
                            ) : (
                                <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                                    {recentApplications.map((application, index) => (
                                        <React.Fragment key={application.id}>
                                            <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: '#48bb78' }}>
                                                        <ApplicationIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                                {application.formName || 'Application'}
                                                            </Typography>
                                                            <Chip
                                                                label="New"
                                                                color="info"
                                                                size="small"
                                                                icon={<Warning />}
                                                            />
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography variant="body2" color="text.secondary">
                                                            {application.submittedBy || 'Anonymous'} • {application.formType} • {new Date(application.createdAt).toLocaleDateString()}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                            {index < recentApplications.length - 1 && <Divider variant="inset" component="li" />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* System Overview */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                                System Overview
                            </Typography>
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Forms Publication Rate
                                        </Typography>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={stats.totalForms > 0 ? (stats.publishedForms / stats.totalForms) * 100 : 0}
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            {stats.publishedForms} of {stats.totalForms} forms published
                                        </Typography>
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Application Activity
                                        </Typography>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={75}
                                            color="success"
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            High activity this month
                                        </Typography>
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            System Health
                                        </Typography>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={95}
                                            color="info"
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            All systems operational
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DashboardOverview;
